import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import DOMPurify from 'dompurify';
import { useMediaQuery } from 'react-responsive';
import { getShortenedString } from '../../../../models/UiUtils';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { SimpleTooltip } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { styleSheet } from './styles';

export const CampaignTemplateCardTruncatedBody = ({
	style,
	showReadMore = true,
	text,
	truncated,
	fullText,
	useTwoLineDisplay = false,
}: {
	style?: StyleDeclarationValue[];
	showReadMore?: boolean;
	text: string;
	truncated: boolean;
	fullText?: string;
	useTwoLineDisplay?: boolean;
}) => {
	const contentElement = (
		<p className={css(styleSheet.textContent, useTwoLineDisplay && styleSheet.textContentTwoLines, ...(style || []))}>
			<span>
				{text}
				{truncated && !useTwoLineDisplay ? '... ' : ''}
			</span>
			{truncated && showReadMore && !useTwoLineDisplay ? (
				<strong className={css(baseStyleSheet.fontBold)} dangerouslySetInnerHTML={{ __html: 'Read more' }} />
			) : null}
		</p>
	);

	// If we have full text and are using two-line display, wrap with tooltip
	if (useTwoLineDisplay && fullText && fullText !== text) {
		return (
			<SimpleTooltip title={fullText} styles={[tooltipStyles.contentWithMaxWidth]}>
				{contentElement}
			</SimpleTooltip>
		);
	}

	return contentElement;
};

export const CampaignTemplateCardMenu = ({
	card,
	onDelete,
	onEditUserGroups,
	onShare,
	userSession,
	userGroups,
}: {
	card: ITemplateCard;
	onDelete?: () => void;
	onEditUserGroups?: () => void;
	onShare?: (val: boolean) => void;
	userSession: Api.UserSessionContext;
	userGroups?: Api.IUserGroup[];
}) => {
	const isAdminOrCreator = card.creator?.id === userSession?.user?.id || userSession?.isAdmin;
	const canShare = onShare && isAdminOrCreator;
	const hasUserGroups = !!userGroups?.find(g => !g.isPredefined);
	const canEditUserGroups = hasUserGroups && userSession?.isAdmin && onEditUserGroups;

	return (
		<div className={css(styleSheet.menu)}>
			<Dropdown>
				<DropdownEllipsisTrigger />
				<DropdownContent>
					<DropdownItem onClick={() => onDelete?.()}>Delete</DropdownItem>
					{!canEditUserGroups && canShare && card?.scope === Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(false)}>Unshare</DropdownItem>
					) : null}
					{!canEditUserGroups && canShare && card?.scope !== Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(true)}>Share</DropdownItem>
					) : null}
					{hasUserGroups && canEditUserGroups ? (
						<DropdownItem onClick={() => onEditUserGroups()}>Permissions</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
		</div>
	);
};

/** Mirrors the server's allowlist, so a regression there cannot turn a template body into script on this page. */
const PREVIEW_ALLOWED_TAGS = [
	'b',
	'strong',
	'i',
	'em',
	'u',
	'br',
	'p',
	'ul',
	'ol',
	'li',
	'a',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'span',
];
const PREVIEW_ALLOWED_ATTR = ['data-placeholder', 'data-placeholder-value'];

/**
 * The account-template preview: the server-built preview markup, clipped to three lines by CSS.
 */
export const CampaignSummaryFromPreview = ({ card }: { card: ITemplateCard }) => {
	if (!card?.previewHtml) {
		return null;
	}

	const previewHtml = DOMPurify.sanitize(card.previewHtml, {
		ALLOWED_ATTR: PREVIEW_ALLOWED_ATTR,
		ALLOWED_TAGS: PREVIEW_ALLOWED_TAGS,
	});

	return <div className={css(styleSheet.previewHtml)} dangerouslySetInnerHTML={{ __html: previewHtml }} />;
};

export const CampaignSummaryFromContentShort = ({ card }: { card: ITemplateCard }) => {
	const isLargeScreen = useMediaQuery({ minWidth: 1200 });
	const maxCharCount = isLargeScreen ? 120 : 100;
	// Curated summary only: these are library templates, where it is authored rather than derived.
	const summary = card?.summary;
	const shortSummary = summary ? getShortenedString(summary?.trim(), maxCharCount) : '';
	return (
		<div className={css(styleSheet.textContainer)}>
			<CampaignTemplateCardTruncatedBody
				style={[styleSheet.textContentSmall, styleSheet.textContentThreeLines]}
				text={shortSummary}
				truncated={summary?.length >= maxCharCount}
				showReadMore={false}
			/>
		</div>
	);
};
